import React, { useState, useMemo, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { REFERENCES, SERVICE_KEYS } from "../lib/data";
import { MapPin, X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";

export default function References() {
  const { t, lang } = useLang();
  const [filter, setFilter] = useState("all");
  const [activeIdx, setActiveIdx] = useState(null);

  const filtered = useMemo(() => {
    if (filter === "all") return REFERENCES;
    return REFERENCES.filter((r) => r.category === filter);
  }, [filter]);

  const openLightbox = useCallback((idx) => setActiveIdx(idx), []);
  const closeLightbox = useCallback(() => setActiveIdx(null), []);
  const nextImage = useCallback(() => {
    setActiveIdx((i) => (i === null ? 0 : (i + 1) % filtered.length));
  }, [filtered.length]);
  const prevImage = useCallback(() => {
    setActiveIdx((i) => (i === null ? 0 : (i - 1 + filtered.length) % filtered.length));
  }, [filtered.length]);

  // Keyboard navigation
  useEffect(() => {
    if (activeIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [activeIdx, closeLightbox, nextImage, prevImage]);

  const activeItem = activeIdx !== null ? filtered[activeIdx] : null;

  return (
    <div data-testid="page-references">
      {/* Header */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#0022FF] mb-4">
              <span className="h-px w-8 bg-[#0022FF]" />
              {t.nav.references}
            </div>
            <h1 className="font-display font-black text-slate-900 text-5xl sm:text-6xl lg:text-7xl tracking-tight">
              {t.references.title}
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">{t.references.subtitle}</p>
          </div>

          {/* Filters */}
          <div className="mt-10 flex flex-wrap gap-2">
            <button
              data-testid="filter-all"
              onClick={() => {
                setFilter("all");
                setActiveIdx(null);
              }}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                filter === "all"
                  ? "bg-[#0022FF] text-white shadow-lg"
                  : "bg-white border border-slate-200 text-slate-700 hover:border-[#0022FF]"
              }`}
            >
              {t.references.all}
            </button>
            {SERVICE_KEYS.map((k) => (
              <button
                key={k}
                data-testid={`filter-${k}`}
                onClick={() => {
                  setFilter(k);
                  setActiveIdx(null);
                }}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  filter === k
                    ? "bg-[#0022FF] text-white shadow-lg"
                    : "bg-white border border-slate-200 text-slate-700 hover:border-[#0022FF]"
                }`}
              >
                {t.services[k].name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filtered.length === 0 ? (
            <p className="text-center text-slate-500 py-20">{t.references.noResults}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="references-grid">
              <AnimatePresence mode="popLayout">
                {filtered.map((r, i) => (
                  <motion.button
                    key={r.id}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
                    onClick={() => openLightbox(i)}
                    className={`group relative overflow-hidden rounded-3xl border border-slate-200 bg-white text-left cursor-zoom-in focus:outline-none focus:ring-4 focus:ring-[#0022FF]/30 ${
                      i % 5 === 0 ? "sm:row-span-2" : ""
                    }`}
                    data-testid={`reference-card-${r.id}`}
                    aria-label={`${r.title[lang]} vergrössern`}
                  >
                    <div className={`relative overflow-hidden ${i % 5 === 0 ? "h-72 sm:h-full" : "h-64"}`}>
                      <img
                        src={r.image}
                        alt={r.title[lang]}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0022FF]/90 via-[#0022FF]/20 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
                      <div className="absolute top-4 left-4">
                        <span className="inline-block rounded-full bg-white/90 backdrop-blur-md px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#0022FF]">
                          {t.services[r.category].name}
                        </span>
                      </div>
                      <div className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/95 backdrop-blur flex items-center justify-center text-[#0022FF] opacity-0 group-hover:opacity-100 transition-all group-hover:scale-110 shadow-lg">
                        <ZoomIn className="h-4 w-4" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                        <div className="flex items-center gap-2 text-xs text-sky-200 mb-2">
                          <MapPin className="h-3 w-3" />
                          {r.location} · {r.year}
                        </div>
                        <h3 className="font-display font-bold text-xl tracking-tight">
                          {r.title[lang]}
                        </h3>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {activeItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-slate-950/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
            onClick={closeLightbox}
            data-testid="reference-lightbox"
          >
            {/* Close */}
            <button
              onClick={closeLightbox}
              data-testid="lightbox-close"
              className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-white flex items-center justify-center transition-colors"
              aria-label="Schließen"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Prev / Next */}
            {filtered.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                  data-testid="lightbox-prev"
                  className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-10 h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-white flex items-center justify-center transition-colors"
                  aria-label="Vorheriges"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                  data-testid="lightbox-next"
                  className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-10 h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-white flex items-center justify-center transition-colors"
                  aria-label="Nächstes"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}

            {/* Content */}
            <motion.div
              key={activeItem.id}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.25 }}
              className="relative max-w-6xl w-full max-h-[85vh] flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={activeItem.image}
                alt={activeItem.title[lang]}
                data-testid="lightbox-image"
                className="max-h-[75vh] w-auto object-contain rounded-2xl shadow-2xl"
              />
              <div className="mt-4 w-full max-w-3xl text-center">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white/90 mb-3">
                  {t.services[activeItem.category].name}
                </div>
                <h3 className="font-display font-bold text-white text-xl sm:text-2xl tracking-tight">
                  {activeItem.title[lang]}
                </h3>
                <p className="mt-1 text-sm text-white/60 flex items-center justify-center gap-2">
                  <MapPin className="h-3 w-3" />
                  {activeItem.location} · {activeItem.year}
                </p>
                <p className="mt-3 text-xs text-white/40">
                  {activeIdx + 1} / {filtered.length}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
